"use client";

import { useEffect, useRef, useState } from "react";
import { useInView } from "./Reveal";

interface CountUpProps {
  /** Final value; rendered in the markup from the start (progressive
   *  enhancement, not injection), so crawlers, no-JS clients and
   *  reduced-motion users always see the real number. */
  value: number;
  /** Animation length in ms. Brief default 1600. */
  duration?: number;
  prefix?: string;
  suffix?: string;
  /** Decimal places to preserve while counting. */
  decimals?: number;
  className?: string;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

function format(n: number, decimals: number): string {
  return n.toLocaleString("en-NZ", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * SI-04 count-up stat numeral. Counts 0 to value over ~1.6s via a
 * single rAF loop once 50% visible, with thousands separators
 * preserved. Pair with the .silk-stat type class:
 *
 *   <p className="silk-stat"><CountUp value={2600} suffix="+" /></p>
 */
export function CountUp({
  value,
  duration = 1600,
  prefix = "",
  suffix = "",
  decimals = 0,
  className,
}: CountUpProps) {
  const [ref, inView] = useInView<HTMLSpanElement>({
    threshold: 0.5,
    rootMargin: "0px",
    once: true,
  });
  const [display, setDisplay] = useState(() => format(value, decimals));
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;

    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return; // final value is already rendered
    }

    let raf = 0;
    const startTime = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = easeOutCubic(t);
      const factor = Math.pow(10, decimals);
      const current = Math.round(value * eased * factor) / factor;
      setDisplay(format(current, decimals));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      }
    };
    // The final value stays visible until the first frame lands; the
    // first tick (t near 0) snaps the numeral to 0 and the count begins.
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, decimals]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
